import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ExternalLink, BookOpen, FileText, Info, Calendar, 
  Globe, Users, GraduationCap, ArrowRight, Search,
  Headphones, Mic, PenTool
} from "lucide-react";
import NYReadinessCard from "@/components/NYReadinessCard";

const Resources = () => {
  const officialLinks = [
    {
      title: "WIDA Official Website",
      url: "https://wida.wisc.edu",
      description: "WIDA Consortium homepage — standards, assessments, and professional development.",
      category: "WIDA"
    },
    {
      title: "WIDA ACCESS for ELLs",
      url: "https://wida.wisc.edu/assess/access",
      description: "Official information about the WIDA ACCESS assessment structure, scoring, and administration.",
      category: "WIDA"
    },
    {
      title: "WIDA Practice & Sample Items",
      url: "https://wida.wisc.edu/assess/access/practice",
      description: "Free official practice items from WIDA to familiarize students and teachers with the test format.",
      category: "WIDA"
    },
    {
      title: "WIDA Screener Overview",
      url: "https://wida.wisc.edu/assess/screener",
      description: "Information about the WIDA Screener used for initial ELL identification at enrollment.",
      category: "WIDA"
    },
    {
      title: "WIDA ELD Standards Framework",
      url: "https://wida.wisc.edu/teach/standards/eld",
      description: "The English Language Development Standards that underpin all WIDA assessments.",
      category: "WIDA"
    },
    {
      title: "WIDA Can Do Descriptors",
      url: "https://wida.wisc.edu/teach/can-do/descriptors",
      description: "Proficiency-level descriptors teachers can use to differentiate instruction for ELLs.",
      category: "WIDA"
    },
  ];

  const nysedLinks = [
    {
      title: "NYSED WIDA Consortium Info",
      url: "https://www.nysed.gov/state-assessment/wida-consortium",
      description: "Official NY State Education Department page on the transition to WIDA assessments.",
      category: "NYSED"
    },
    {
      title: "NYSED Office of Bilingual Education",
      url: "https://www.nysed.gov/bilingual-ed",
      description: "Resources for educators serving multilingual learners in New York State.",
      category: "NYSED"
    },
    {
      title: "NYSED ELL/MLL Resources",
      url: "https://www.nysed.gov/bilingual-ed/english-language-learnermultilingual-learner-resource-guide",
      description: "Comprehensive resource guide for supporting English Language Learners in NY schools.",
      category: "NYSED"
    },
    {
      title: "CR Part 154 Regulations",
      url: "https://www.nysed.gov/bilingual-ed/cr-part-154-comprehensive-guide",
      description: "Commissioner's Regulations governing ELL education and services in New York State.",
      category: "NYSED"
    },
  ];

  const timeline = [
    { date: "2024-2025", event: "NY State announces transition from NYSESLAT to WIDA", status: "completed" },
    { date: "Spring 2026", event: "Final NYSESLAT Administration", status: "upcoming" },
    { date: "2026-2027", event: "Transition year — teacher training and preparation", status: "future" },
    { date: "Spring 2027", event: "First WIDA ACCESS Administration in NY", status: "future" },
    { date: "Fall 2027", event: "WIDA Screener replaces NYSITELL for initial identification", status: "future" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-6 py-12" data-testid="resources-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-3 text-sm text-gray-500 mb-8">
        <Link to="/" className="hover:text-[#002FA7]">Home</Link>
        <span>/</span>
        <span className="text-gray-900">Resources</span>
      </div>

      {/* Header */}
      <div className="mb-10">
        <h1 className="text-3xl sm:text-4xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Information & Resources
        </h1>
        <p className="text-gray-600 max-w-3xl">
          Official resources, guides, and links for educators preparing students for New York's new 2027 English Language Proficiency Assessment. 
          Everything you need to support multilingual learners.
        </p>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
        <Link to="/grades" className="block">
          <div className="border-2 border-gray-200 p-5 hover:border-[#002FA7] transition-colors h-full" data-testid="quick-link-practice">
            <BookOpen size={24} className="text-[#002FA7] mb-3" />
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Practice ACCESS</h3>
            <p className="text-sm text-gray-500">Full practice exams across all grades and modalities</p>
          </div>
        </Link>
        <Link to="/screener" className="block">
          <div className="border-2 border-gray-200 p-5 hover:border-[#002FA7] transition-colors h-full" data-testid="quick-link-screener">
            <Search size={24} className="text-[#002FA7] mb-3" />
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>WIDA Screener</h3>
            <p className="text-sm text-gray-500">Initial identification tool for potential ELLs</p>
          </div>
        </Link>
        <Link to="/teacher-key" className="block">
          <div className="border-2 border-gray-200 p-5 hover:border-[#002FA7] transition-colors h-full" data-testid="quick-link-teacher-key">
            <GraduationCap size={24} className="text-[#002FA7] mb-3" />
            <h3 className="font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Teacher Keys</h3>
            <p className="text-sm text-gray-500">Answer keys and rubrics for all grade levels</p>
          </div>
        </Link>
      </div>

      {/* MOSAIC-NY1 · Pillar 1 · NY 2027 Readiness Center */}
      <div className="mb-10" data-testid="ny-readiness-center-section">
        <NYReadinessCard />
      </div>

      {/* NY State Transition Timeline */}
      <div className="border-2 border-gray-200 mb-10" data-testid="transition-timeline">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Calendar size={22} className="text-[#002FA7]" />
            NY State WIDA Transition Timeline
          </h2>
        </div>
        <div className="p-6">
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 top-3 bottom-3 w-0.5 bg-gray-200"></div>
            
            <div className="space-y-6">
              {timeline.map((item, idx) => (
                <div key={idx} className="flex gap-5 relative" data-testid={`timeline-item-${idx}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 
                    ${item.status === 'completed' ? 'bg-green-100 border-2 border-green-500' : 
                      item.status === 'upcoming' ? 'bg-yellow-100 border-2 border-yellow-500' : 
                      'bg-gray-100 border-2 border-gray-300'}`}>
                    <div className={`w-2.5 h-2.5 rounded-full 
                      ${item.status === 'completed' ? 'bg-green-500' : 
                        item.status === 'upcoming' ? 'bg-yellow-500' : 'bg-gray-400'}`}></div>
                  </div>
                  <div>
                    <div className={`text-sm font-semibold 
                      ${item.status === 'completed' ? 'text-green-700' : 
                        item.status === 'upcoming' ? 'text-yellow-700' : 'text-gray-500'}`}>
                      {item.date}
                    </div>
                    <div className="text-gray-800 font-medium">{item.event}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* WIDA Assessment Overview */}
      <div className="border-2 border-gray-200 mb-10">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Info size={22} className="text-[#002FA7]" />
            2027 English Language Proficiency Assessment Overview
          </h2>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h3 className="font-bold mb-2">What is WIDA ACCESS?</h3>
            <p className="text-sm text-gray-600">
              WIDA ACCESS for ELLs (Assessing Comprehension and Communication in English State-to-State) 
              is an annual English language proficiency assessment administered to K-12 students identified 
              as English Language Learners. It measures proficiency across four language domains: Listening, 
              Speaking, Reading, and Writing.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-4">
            {[
              { icon: Headphones, name: "Listening", desc: "Comprehension of spoken English in academic settings" },
              { icon: Mic, name: "Speaking", desc: "Oral production in social and academic contexts" },
              { icon: BookOpen, name: "Reading", desc: "Understanding of written English texts" },
              { icon: PenTool, name: "Writing", desc: "Written production at word, sentence, and discourse levels" },
            ].map(d => (
              <div key={d.name} className="border border-gray-200 p-4">
                <d.icon size={20} className="text-[#002FA7] mb-2" />
                <div className="font-semibold text-sm">{d.name}</div>
                <div className="text-xs text-gray-500 mt-1">{d.desc}</div>
              </div>
            ))}
          </div>
          <div>
            <h3 className="font-bold mb-2">Scoring</h3>
            <p className="text-sm text-gray-600">
              Students receive scores on a 1.0 to 6.0 scale for each domain, plus composite scores for 
              Oral Language (Listening + Speaking), Literacy (Reading + Writing), Comprehension (Listening + Reading), 
              and an Overall score. WIDA proficiency levels range from Level 1 (Entering) to Level 6 (Reaching).
            </p>
          </div>
        </div>
      </div>

      {/* Official WIDA Resources */}
      <div className="border-2 border-gray-200 mb-10" data-testid="wida-resources-section">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Globe size={22} className="text-[#002FA7]" />
            Official WIDA Resources
          </h2>
        </div>
        <div className="divide-y divide-gray-100">
          {officialLinks.map((link, idx) => (
            <a
              key={idx}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start justify-between p-5 hover:bg-blue-50 transition-colors group"
              data-testid={`wida-link-${idx}`}
            >
              <div className="flex-1 mr-4">
                <div className="font-semibold group-hover:text-[#002FA7] transition-colors">{link.title}</div>
                <p className="text-sm text-gray-500 mt-1">{link.description}</p>
              </div>
              <ExternalLink size={18} className="text-gray-300 group-hover:text-[#002FA7] flex-shrink-0 mt-1" />
            </a>
          ))}
        </div>
      </div>

      {/* NYSED Resources */}
      <div className="border-2 border-gray-200 mb-10" data-testid="nysed-resources-section">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <FileText size={22} className="text-[#002FA7]" />
            NY State Education Department (NYSED)
          </h2>
        </div>
        <div className="divide-y divide-gray-100">
          {nysedLinks.map((link, idx) => (
            <a
              key={idx}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start justify-between p-5 hover:bg-blue-50 transition-colors group"
              data-testid={`nysed-link-${idx}`}
            >
              <div className="flex-1 mr-4">
                <div className="font-semibold group-hover:text-[#002FA7] transition-colors">{link.title}</div>
                <p className="text-sm text-gray-500 mt-1">{link.description}</p>
              </div>
              <ExternalLink size={18} className="text-gray-300 group-hover:text-[#002FA7] flex-shrink-0 mt-1" />
            </a>
          ))}
        </div>
      </div>

      {/* For Educators */}
      <div className="bg-[#002FA7] text-white p-8 mb-10" data-testid="educator-cta">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Ready to Prepare Your Students?
            </h2>
            <p className="text-blue-100 max-w-lg">
              Use MOSAIC's practice exams to familiarize students with the WIDA format before the Spring 2027 administration.
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/grades">
              <Button className="btn-sharp bg-white text-[#002FA7] border-white hover:bg-gray-100 h-12 px-6" data-testid="resources-start-practice-btn">
                Start Practice
                <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/screener">
              <Button variant="outline" className="btn-sharp border-white text-white hover:bg-white/10 h-12 px-6" data-testid="resources-start-screener-btn">
                Screener
                <Search size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="border-2 border-gray-200" data-testid="faq-section">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Frequently Asked Questions
          </h2>
        </div>
        <div className="divide-y divide-gray-100">
          {[
            {
              q: "When will NY schools start using WIDA ACCESS?",
              a: "The first WIDA ACCESS administration in New York will be in Spring 2027. The 2026-2027 school year is the transition year for training and preparation."
            },
            {
              q: "What happens to NYSESLAT scores?",
              a: "The final NYSESLAT will be administered in Spring 2026. Historical NYSESLAT scores will remain in student records. WIDA scores use a different scale (1.0-6.0)."
            },
            {
              q: "How is the WIDA Screener different from ACCESS?",
              a: "The Screener is a shorter assessment used for initial ELL identification at enrollment. ACCESS is the annual proficiency test given to all identified ELLs each spring."
            },
            {
              q: "What WIDA level is needed to exit ELL services?",
              a: "In most states, a composite score of 4.5 or higher on WIDA ACCESS, combined with other criteria, is used to determine readiness to exit ELL services. NY State will publish specific criteria."
            },
            {
              q: "Is the WIDA assessment online or paper?",
              a: "WIDA ACCESS offers both online and paper formats. Listening, Reading, and Writing are available online with semi-adaptive testing. Speaking is administered one-on-one."
            },
          ].map((faq, idx) => (
            <details key={idx} className="group" data-testid={`faq-item-${idx}`}>
              <summary className="p-5 font-semibold cursor-pointer hover:bg-gray-50 flex items-center justify-between">
                {faq.q}
                <ArrowRight size={16} className="text-gray-400 group-open:rotate-90 transition-transform" />
              </summary>
              <div className="px-5 pb-5 text-sm text-gray-600">
                {faq.a}
              </div>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Resources;
